import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, onSnapshot, updateDoc, doc } from "firebase/firestore";
import PhotoGallery from "../components/PhotoGallery";
import TimeTakenField from "./TimeTakenField";
import {
  HOURS_MIN,
  buildTimeTakenCore,
  validateTimeTaken,
} from "./timeEntry";

const ENGINEERS = ["Raf", "Kate", "John", "Rob", "Martin", "Will"];
const NUCLEAR_STOPPAGE_MODE = true;
const APP_TITLE = "Jobs & Requests";

function emptyTimeTaken() {
  return { value: HOURS_MIN, unit: "hours", exceeded: false, note: "" };
}

const STOPPAGE_STYLES = `
/* Soft card pulse (you already had this) */
@keyframes stoppageFlash {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,.35); }
  50%      { box-shadow: 0 0 0 10px rgba(239,68,68,.10); }
}
.stoppage-flash { animation: stoppageFlash 1.6s ease-in-out infinite; }

/* Full-screen lockdown overlay shake */
@keyframes harshShake {
  0% { transform: translate(0,0); }
  20% { transform: translate(2px,-2px); }
  40% { transform: translate(-2px,2px); }
  60% { transform: translate(2px,2px); }
  80% { transform: translate(-2px,-2px); }
  100% { transform: translate(0,0); }
}
.lockdown-shake { animation: harshShake .4s linear infinite; }

@media (prefers-reduced-motion: reduce) {
  .stoppage-flash { animation: none !important; }
  .lockdown-shake { animation: none !important; }
}
`;



export default function JobsAndRequestsPro() {
  // BREAKDOWNS
  const [bd, setBd] = useState({ new: [], inProgress: [], complete: [] });
  const [alarmPlayed, setAlarmPlayed] = useState(false);

  // WORK REQUESTS
  const [wr, setWr] = useState({ new: [], inProgress: [], complete: [] });

  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [timeTaken, setTimeTaken] = useState(emptyTimeTaken);
  const [timeTakenValid, setTimeTakenValid] = useState(true);

  // Slide-over state (shared)
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelItem, setPanelItem] = useState(null); // {kind, data}
  const [notesDraft, setNotesDraft] = useState("");

  // ───────────────────────────── BREAKDOWNS stream
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "machines"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const newOnes = list
        .filter((j) => j.status === "new")
        .sort((a, b) => {
          if (a.faultType === "Stoppage" && b.faultType !== "Stoppage") return -1;
          if (a.faultType !== "Stoppage" && b.faultType === "Stoppage") return 1;
          return 0;
        });

      setBd({
        new: newOnes,
        inProgress: list.filter((j) => j.status === "inprogress"),
        complete: list
          .filter((j) => j.status === "complete")
          .sort((a, b) => {
            const da = a.dateOfCompletion?.toDate?.() || new Date(0);
            const dbb = b.dateOfCompletion?.toDate?.() || new Date(0);
            return dbb - da;
          }),
      });

      const hasStoppage = newOnes.some((j) => (j.faultType || "").toLowerCase() === "stoppage");
      if (hasStoppage && !alarmPlayed) {
        new Audio("/alarm.mp3").play().catch(() => {});
        setAlarmPlayed(true);
      }
    });
    return () => unsub();
  }, [alarmPlayed]);

// Snooze (persisted)
const [snoozeUntil, setSnoozeUntil] = useState(() => {
  const raw = localStorage.getItem("stoppageSnoozeUntil");
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) ? n : 0;
});

// Tick every second so countdown updates + auto unsnooze
useEffect(() => {
  const id = setInterval(() => {
    // force render while snoozed; also auto-clear past snooze
    if (snoozeUntil && Date.now() >= snoozeUntil) {
      setSnoozeUntil(0);
      localStorage.removeItem("stoppageSnoozeUntil");
    } else {
      // noop state nudge: updating a dummy state would be noisy; instead rely on
      // the lock visibility calc below which runs each render, and this interval triggers renders
      // If you want explicit re-render, uncomment next line:
      // setTick((t) => t + 1);
    }
  }, 1000);
  return () => clearInterval(id);
}, [snoozeUntil]);

const isSnoozed = snoozeUntil && Date.now() < snoozeUntil;
const snoozeRemainingMs = isSnoozed ? snoozeUntil - Date.now() : 0;

  // ───────────────────────────── WORK REQUESTS stream
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "work_requests"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const newOnes = list
        .filter((j) => j.status === "new")
        .sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority));

      setWr({
        new: newOnes,
        inProgress: list.filter((j) => j.status === "inprogress"),
        complete: list
          .filter((j) => j.status === "complete")
          .sort((a, b) => {
            const da = a.dateOfCompletion?.toDate?.() || new Date(0);
            const dbb = b.dateOfCompletion?.toDate?.() || new Date(0);
            return dbb - da;
          }),
      });
    });
    return () => unsub();
  }, []);

  // ───────────────────────────── Actions
  const openPanel = (kind, item) => {
    setPanelItem({ kind, data: item });
    setNotesDraft(item.engineerNotes || "");
    setPanelOpen(true);
  };
  
  const isStoppage = (j) => (j.faultType || "").toLowerCase() === "stoppage";

  // Compute NEW stoppages
const newStoppages = useMemo(
  () => bd.new.filter(j => (j.faultType || "").toLowerCase() === "stoppage"),
  [bd.new]
);

const lockdownVisible = NUCLEAR_STOPPAGE_MODE && newStoppages.length > 0 && !isSnoozed;

const SNOOZE_MINUTES = 3;
const snoozeNow = () => {
  const until = Date.now() + SNOOZE_MINUTES * 60 * 1000;
  setSnoozeUntil(until);
  localStorage.setItem("stoppageSnoozeUntil", String(until));
};

// Quick-ack handler used by the overlay (acks the oldest NEW stoppage)
const acknowledgeOldestStoppage = async (engineer) => {
  const target = newStoppages[0];
  if (!target) return;
  await acknowledge("bd", target.id, engineer);
};


  const acknowledge = async (kind, id, engineer) => {
    const col = kind === "bd" ? "machines" : "work_requests";
    await updateDoc(doc(db, col, id), {
      status: "inprogress",
      acknowledgedBy: engineer,
      dateOfAcknowledgement: new Date(),
    });
  };

  const saveNotes = async () => {
    if (!panelItem) return;
    const { kind, data } = panelItem;
    const col = kind === "bd" ? "machines" : "work_requests";
    await updateDoc(doc(db, col, data.id), { engineerNotes: notesDraft });
  };

  const openCompleteModal = (kind, id) => {
    setCompleteTarget({ kind, id });
    setTimeTaken(emptyTimeTaken());
    setTimeTakenValid(true);
    setCompleteModalOpen(true);
  };

  const confirmComplete = async () => {
    if (!completeTarget) return;

    const validation = validateTimeTaken(timeTaken);
    if (!validation.ok) return;

    const { kind, id } = completeTarget;

    const col = kind === "bd" ? "machines" : "work_requests";
    const { minutes, ...timeTakenFields } = buildTimeTakenCore(timeTaken);

    await updateDoc(doc(db, col, id), {
      status: "complete",
      dateOfCompletion: new Date(),
      ...timeTakenFields,
      timeTakenMinutes: minutes,
    });

    setCompleteModalOpen(false);
    setCompleteTarget(null);
    setPanelOpen(false);
  };

  // ───────────────────────────── Derived counts (counters STILL include completed)
  const bdCounts = useMemo(
    () => ({ new: bd.new.length, prog: bd.inProgress.length, done: bd.complete.length }),
    [bd]
  );
  const wrCounts = useMemo(
    () => ({ new: wr.new.length, prog: wr.inProgress.length, done: wr.complete.length }),
    [wr]
  );

return (
  <>
    <style>{STOPPAGE_STYLES}</style>

<StoppageLockdown
  visible={lockdownVisible}
  remainingCount={newStoppages.length}
  sampleItem={newStoppages[0]}
  engineers={ENGINEERS}
  onAcknowledge={acknowledgeOldestStoppage}
  onSnooze={snoozeNow}
  snoozeMinutes={SNOOZE_MINUTES}
  snoozeRemainingMs={snoozeRemainingMs}
/>

    
    <div className="min-h-screen bg-gray-50 p-6">


      {/* Header metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <MetricCard
          title="Breakdowns"
          counts={bdCounts}
          hot={bd.new.some((j) => (j.faultType || "").toLowerCase() === "stoppage")}
        />
        <MetricCard title="Work Requests" counts={wrCounts} />
      </div>

      {/* Boards (NO archived lanes) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* BREAKDOWNS */}
        <Board
          title="Breakdowns"
          prominent
          hasAlert={bd.new.some((j) => (j.faultType || "").toLowerCase() === "stoppage")}
        >
          <Kanban>
            {/* New */}
            <Column title="New">
              {bd.new.map((j) => (
                <Card
                  key={j.id}
                  tone={isStoppage(j) ? "stoppage" : "default"}
                  showBadge={isStoppage(j)}
                  flashing={isStoppage(j)}
                >
                  <CardHeader
                    title={`${j.machine} — ${j.fault}`}
                    meta={`${j.timestamp?.toDate?.()?.toLocaleString?.() || ""}`}
                    chips={[
                      j.faultType
                        ? {
                            label: j.faultType,
                            tone: j.faultType === "Stoppage" ? "danger" : "warn",
                          }
                        : null,
                    ]}
                  />
                  {j.notes && <p className="text-sm text-gray-600 line-clamp-2">{j.notes}</p>}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {ENGINEERS.map((e) => (
                      <Pill key={e} onClick={() => acknowledge("bd", j.id, e)}>
                        {e}
                      </Pill>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button onClick={() => openPanel("bd", j)}>Details</Button>
                  </div>
                </Card>
              ))}
            </Column>

            {/* In Progress */}
            <Column title="In Progress">
              {bd.inProgress.map((j) => (
                <Card
                key={j.id}
                tone={isStoppage(j) ? "stoppage" : "default"}
                >

                  <CardHeader
                    title={`${j.machine} — ${j.fault}`}
                    meta={`Ack: ${j.acknowledgedBy || "—"}`}
                    chips={[{ label: "In progress", tone: "info" }]}
                  />
                  <div className="mt-3 flex justify-between">
                    <Button variant="ghost" onClick={() => openPanel("bd", j)}>
                      Notes / Details
                    </Button>
                    <Button variant="success" onClick={() => openCompleteModal("bd", j.id)}>
                      Complete
                    </Button>
                  </div>
                </Card>
              ))}
            </Column>
          </Kanban>
        </Board>

        {/* WORK REQUESTS */}
        <Board title="Work Requests">
          <Kanban>
            {/* New */}
            <Column title="New">
              {wr.new.map((j) => (
                <Card key={j.id}>
                  <CardHeader
                    title={`${j.requestType || "Request"} — ${j.location || "Location N/A"}`}
                    meta={`${j.timestamp?.toDate?.()?.toLocaleString?.() || ""}`}
                    chips={[
                      j.priority ? { label: j.priority, tone: toneForPriority(j.priority) } : null,
                    ]}
                  />
                  {j.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{j.description}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {ENGINEERS.map((e) => (
                      <Pill key={e} onClick={() => acknowledge("wr", j.id, e)}>
                        {e}
                      </Pill>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-between">
                    {j.sharepoint?.shareUrl ? (
                      <a
                        className="text-sm underline text-indigo-700"
                        href={j.sharepoint.shareUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Upload photos
                      </a>
                    ) : (
                      <span />
                    )}
                    <Button onClick={() => openPanel("wr", j)}>Details</Button>
                  </div>
                </Card>
              ))}
            </Column>

            {/* In Progress */}
            <Column title="In Progress">
              {wr.inProgress.map((j) => (
                <Card key={j.id}>
                  <CardHeader
                    title={`${j.requestType || "Request"} — ${j.location || "Location N/A"}`}
                    meta={`Ack: ${j.acknowledgedBy || "—"}`}
                    chips={[{ label: "In progress", tone: "info" }]}
                  />
                  <div className="mt-2 flex items-center justify-between">
                    {j.sharepoint?.folderItemId ? (
                      <Button variant="ghost" onClick={() => openPanel("wr", j)}>
                        Photos & Notes
                      </Button>
                    ) : (
                      <span />
                    )}
                    <Button variant="success" onClick={() => openCompleteModal("wr", j.id)}>
                      Complete
                    </Button>
                  </div>
                </Card>
              ))}
            </Column>
          </Kanban>
        </Board>
      </div>

      {/* Slide-over panel */}
      <SlideOver
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={panelItem ? panelTitle(panelItem) : ""}
      >
        {panelItem && (
          <div className="space-y-4">
            <DetailList item={panelItem} />

            {/* Work Request photos */}
            {panelItem.kind === "wr" && panelItem.data?.sharepoint?.folderItemId && (
              <>
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Photos</h4>
                  {panelItem.data.sharepoint?.shareUrl && (
                    <a
                      className="text-sm underline text-indigo-700"
                      href={panelItem.data.sharepoint.shareUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Upload photos
                    </a>
                  )}
                </div>
                <PhotoGallery folderItemId={panelItem.data.sharepoint.folderItemId} />
              </>
            )}

            {/* Notes editor (in-progress states) */}
            {panelItem.data.status === "inprogress" && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Engineer Notes</label>
                <textarea
                  className="w-full p-2 border rounded-md"
                  rows={4}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={saveNotes}>Save Notes</Button>
                  <Button
                      variant="success"
                      onClick={() => openCompleteModal(panelItem.kind, panelItem.data.id)}
                    >
                    Complete
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </SlideOver>
    </div>
    {/* Complete Job Modal */}
{completeModalOpen && (
  <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Time Taken
      </h3>

      <p className="text-sm text-gray-600 mb-4">
        Select the total time taken to complete this job.
      </p>

      <div className="mb-6">
        <TimeTakenField
          value={timeTaken}
          onChange={(nextTimeTaken, isValid) => {
            setTimeTaken(nextTimeTaken);
            setTimeTakenValid(isValid);
          }}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => setCompleteModalOpen(false)}
        >
          Cancel
        </Button>

        <Button
          variant="success"
          onClick={confirmComplete}
          disabled={!timeTakenValid}
        >
          Complete Job
        </Button>
      </div>
    </div>
  </div>
)}
    </>
  );
}

/* ─────────────────────────── UI Helpers ─────────────────────────── */

function MetricCard({ title, counts, hot = false }) {
  return (
    <div className={`border rounded-xl p-5 shadow-sm ${hot ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-lg font-semibold ${hot ? "text-red-800" : "text-gray-900"}`}>{title}</h3>
        {hot && <span className="text-xs font-medium text-red-700">Stoppage present</span>}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <KPI label="New" value={counts.new} tone={hot ? "red" : "indigo"} />
        <KPI label="In Progress" value={counts.prog} tone="amber" />
        <KPI label="Completed" value={counts.done} tone="emerald" />
      </div>
    </div>
  );
}

function KPI({ label, value, tone }) {
  const colors = {
    red: "bg-red-50 text-red-700",
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
  }[tone] || "bg-gray-50 text-gray-700";
  return (
    <div className={`rounded-lg p-4 ${colors} text-center`}>
      <div className="text-2xl font-bold leading-tight">{value}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
}

function Board({ title, children, prominent = false, hasAlert = false }) {
  return (
    <div className={`rounded-xl border shadow-sm ${prominent ? "border-red-300" : "border-gray-200"} bg-white`}>
      <div
        className={`px-5 py-3 rounded-t-xl border-b flex items-center justify-between
        ${prominent ? "bg-red-600 text-white border-red-700" : "bg-gray-50 text-gray-900 border-gray-200"}`}
      >
        <h3 className="text-lg font-semibold tracking-wide uppercase">{title}</h3>
        {prominent && hasAlert && (
          <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded">Stoppage present</span>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/**
 * Two wide lanes (no completed column in this view).
 * Sticky-ish lane headers + generous vertical space.
 */
function Kanban({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

/** Scrollable column: each column manages its own overflow */
function Column({ title, children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 flex flex-col min-h-[520px] max-h-[70vh]">
      <div className="sticky top-[56px] z-10 px-3 pt-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50/90 backdrop-blur border-b border-gray-200">
        {title}
      </div>
      <div className="p-3 overflow-y-auto space-y-3">
        {children && React.Children.count(children) ? children : <Muted>Nothing here.</Muted>}
      </div>
    </div>
  );
}

function Card({ children, tone = "default", showBadge = false, flashing = false }) {
  // tones: "default" | "stoppage"
  const base = "relative rounded-lg border p-3 shadow-sm transition";

  const toneCls = {
    default:  "bg-white border-gray-200 hover:shadow-md",
    stoppage: "bg-red-50 border-red-300 hover:shadow-md",
  }[tone] || "bg-white border-gray-200 hover:shadow-md";

  const flash = flashing ? "stoppage-flash" : "";

  return (
    <div className={`${base} ${toneCls} ${flash}`}>
      {/* Red left bar for stoppages */}
      {tone === "stoppage" && (
        <div className="absolute left-0 top-0 h-full w-1.5 bg-red-500 rounded-l-lg" />
      )}

      {children}

      {/* STOPPAGE badge at bottom center (only when showBadge=true) */}
      {tone === "stoppage" && showBadge && (
        <div className="mt-3 flex justify-center">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide
                           bg-red-600 text-white px-2 py-0.5 rounded shadow-sm">
            ⚠️ Stoppage
          </span>
        </div>
      )}
    </div>
  );
}

function StoppageLockdown({
  visible,
  remainingCount,
  sampleItem,
  engineers,
  onAcknowledge,
  onSnooze,
  snoozeMinutes = 3,
  snoozeRemainingMs = 0,
}) {
  // Alarm + title blink + vibration
  useEffect(() => {
    if (!visible) {
      document.title = APP_TITLE;
      return;
    }
    let tick = false;
    const t = setInterval(() => {
      tick = !tick;
      document.title = tick ? "⚠️ STOPPAGE — ACTION REQUIRED" : APP_TITLE;
    }, 800);

    if (navigator.vibrate) {
      try { navigator.vibrate([200, 100, 200, 100]); } catch {}
    }

    return () => {
      clearInterval(t);
      document.title = APP_TITLE;
    };
  }, [visible]);

  // Try to play alarm on loop (may be blocked until user interacts)
  useEffect(() => {
    if (!visible) return;
    const el = document.getElementById("alarm-audio");
    if (el) {
      el.volume = 0.35;
      el.loop = true;
      el.play().catch(() => {});
    }
    return () => {
      if (el) { try { el.pause(); el.currentTime = 0; } catch {} }
    };
  }, [visible]);

  if (!visible) return null;

  const fmtCountdown = (ms) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(1,"0")}:${String(r).padStart(2,"0")}`;
  };

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-auto">
      <div className="absolute inset-0 bg-red-900/80 backdrop-blur-sm" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-xl border-2 border-red-600 shadow-2xl lockdown-shake">
          <div className="px-5 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-extrabold text-red-700 tracking-wide uppercase">
                ⚠️ Stoppage Detected
              </h2>
              <span className="ml-auto text-sm font-semibold text-red-800 bg-red-100 px-2 py-0.5 rounded">
                {remainingCount} pending
              </span>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-700">
              The application is locked until all <strong>NEW</strong> stoppages are <strong>acknowledged</strong>.
            </p>

            {sampleItem && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="text-sm text-red-900 font-semibold">
                  {sampleItem.machine || "Machine"} — {sampleItem.fault || "Fault"}
                </div>
                <div className="text-xs text-red-800">
                  Reported: {sampleItem.timestamp?.toDate?.()?.toLocaleString?.() || "—"}
                </div>
                {sampleItem.notes && (
                  <div className="text-xs text-red-900 mt-1">
                    Notes: {sampleItem.notes}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {engineers.map((e) => (
                <button
                  key={e}
                  onClick={() => onAcknowledge(e)}
                  className="px-3 py-2 rounded-md border border-red-300 hover:border-red-400 bg-white text-red-700 font-semibold"
                >
                  Acknowledge as {e}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="text-xs text-gray-500">
                Tip: you can still assign from the board, but this overlay will remain until all stoppages are acknowledged.
              </div>
              <div className="flex items-center gap-2">
                {snoozeRemainingMs > 0 && (
                  <span className="text-xs text-gray-600">
                    Snoozed: {fmtCountdown(snoozeRemainingMs)} remaining
                  </span>
                )}
                <button
                  onClick={onSnooze}
                  className="text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50"
                  title={`Hide this overlay for ${snoozeMinutes} minutes`}
                >
                  Snooze {snoozeMinutes}m
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <audio id="alarm-audio" src="/alarm.mp3" />
    </div>
  );
}



function CardHeader({ title, meta, chips = [] }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-medium text-gray-900">{title}</div>
        {meta && <div className="text-xs text-gray-500">{meta}</div>}
      </div>
      <div className="flex flex-wrap gap-1">
        {chips.filter(Boolean).map((c, i) => (
          <Chip key={i} tone={c.tone}>{c.label}</Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ children, tone }) {
  const cls = {
    danger: "bg-red-50 text-red-700 border-red-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    neutral: "bg-gray-50 text-gray-700 border-gray-200",
  }[tone || "neutral"];
  return <span className={`text-xs border rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function Pill({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-xs px-3 py-1 rounded-full border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition"
    >
      {children}
    </button>
  );
}

function Button({ children, onClick, variant = "primary", disabled = false }) {
  const cls = {
    primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
    // slight bump to contrast so it works on red strong tiles too
    ghost: "bg-white/90 border border-gray-300 hover:bg-white text-gray-800",
  }[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-sm px-3 py-1.5 rounded-md disabled:cursor-not-allowed disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}


function Muted({ children, className = "" }) {
  return <p className={`text-sm text-gray-500 ${className}`}>{children}</p>;
}

function SlideOver({ open, onClose, title, children }) {
  return (
    <div className={`${open ? "fixed" : "hidden"} inset-0 z-50`}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-xl border-l border-gray-200 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-base font-semibold text-gray-900">{title}</h4>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function panelTitle(panelItem) {
  if (!panelItem) return "";
  const { kind, data } = panelItem;
  if (kind === "bd") {
    const a = data?.machine || "Machine";
    const b = data?.fault || "";
    return b ? `${a} — ${b}` : a;
  } else {
    const a = data?.requestType || "Request";
    const b = data?.location || "";
    return b ? `${a} — ${b}` : a;
  }
}

function DetailList({ item }) {
  const { kind, data } = item;
  const submitted = data.timestamp?.toDate?.()?.toLocaleString?.() || "N/A";
  const completed = data.dateOfCompletion?.toDate?.()?.toLocaleString?.() || null;

  if (kind === "bd") {
    return (
      <div className="space-y-2 text-sm">
        <Row label="Machine" value={data.machine} />
        <Row label="Fault" value={data.fault} />
        <Row label="Fault Type" value={data.faultType} />
        <Row label="Reported By" value={data.name} />
        <Row label="Submitted" value={submitted} />
        {completed && <Row label="Completed" value={completed} />}
        {data.notes && <Block label="Reported Notes" value={data.notes} />}
        {data.engineerNotes && <Block label="Engineer Notes" value={data.engineerNotes} />}
      </div>
    );
  }
  // work request
  return (
    <div className="space-y-2 text-sm">
      <Row label="Type" value={data.requestType || "Request"} />
      <Row label="Location" value={data.location} />
      <Row label="Priority" value={data.priority} />
      <Row label="Reported By" value={data.name} />
      <Row label="Submitted" value={submitted} />
      {data.dueDate && <Row label="Target Date" value={data.dueDate} />}
      {completed && <Row label="Completed" value={completed} />}
      {data.description && <Block label="Description" value={data.description} />}
      {data.notes && <Block label="Notes" value={data.notes} />}
      {data.engineerNotes && <Block label="Engineer Notes" value={data.engineerNotes} />}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <div className="text-gray-500 w-40">{label}</div>
      <div className="text-gray-900 flex-1">{value || "—"}</div>
    </div>
  );
}
function Block({ label, value }) {
  return (
    <div>
      <div className="text-gray-500 mb-1">{label}</div>
      <div className="text-gray-900 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

/* ─────────────────────────── utils ─────────────────────────── */
function priorityRank(p) {
  const order = { Urgent: 0, High: 1, Normal: 2, Low: 3 };
  return order[p] ?? 9;
}
function toneForPriority(p) {
  return { Urgent: "danger", High: "warn", Normal: "info", Low: "neutral" }[p] || "neutral";
}
